import {
  ArrowUpRight,
  CheckCircle,
  CircleNotch,
  DotsThree,
  PlugsConnected,
  WarningCircle,
} from '@phosphor-icons/react';
import { Dropdown, Modal } from 'antd';
import type { MenuProps } from 'antd';

import {
  CatenaCardState,
  formatTspName,
} from '../../../../../../hooks/useCatenaConnection';

interface CatenaIntegrationCardProps {
  state: CatenaCardState;
  connectedTsps: string[];
  loading: boolean;
  disabled?: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  onSync: () => void;
}

// Explainer for the underlying universal-ELD network, inline at the end of the description rather
// than an eyebrow above the title, so the icon and the primary action share the heading's line.
// Only shown before a link exists — once a customer has connected, they know what Catena is.
function WhatIsCatena() {
  return (
    <a
      href="https://www.catenaclearing.io/"
      target="_blank"
      rel="noopener noreferrer"
      className="ml-1 inline-flex items-center gap-0.5 whitespace-nowrap font-medium text-[#0083bf] hover:underline"
    >
      What is Catena?
      <ArrowUpRight size={12} weight="bold" />
    </a>
  );
}

function CatenaIntegrationCard({
  state,
  connectedTsps,
  loading,
  disabled = false,
  onConnect,
  onDisconnect,
  onSync,
}: CatenaIntegrationCardProps) {
  // Layout flips to side-by-side at md (600px in this project's Tailwind scale), not sm — sm is
  // redefined to 200px here, so an sm: breakpoint would force the row layout onto every phone and
  // squish it. Below md the card stacks: content block on top, full-width actions beneath.
  const card =
    'flex flex-col gap-4 rounded-2xl border bg-white p-4 shadow-sm md:flex-row md:justify-between md:gap-4 md:p-5';

  // Disconnecting a live (or stale) link cancels the Catena share agreement and unlinks trucks,
  // so it must never fire on a single stray click — always confirm first.
  const confirmDisconnect = () => {
    Modal.confirm({
      title: 'Disconnect your ELD?',
      content:
        'This disconnects your provider and stops data syncing. You can reconnect anytime.',
      okText: 'Disconnect',
      okType: 'danger',
      cancelText: 'Keep connected',
      onOk: onDisconnect,
    });
  };

  // Connected-state actions behind a kebab so the card leads with status, not controls. Add
  // provider is benign; Disconnect is destructive and routes through the confirm above.
  const connectedMenuItems: MenuProps['items'] = [
    { key: 'add', label: 'Add another provider' },
    { type: 'divider' },
    { key: 'disconnect', label: 'Disconnect', danger: true },
  ];

  const handleConnectedMenuClick: Required<MenuProps>['onClick'] = ({
    key,
  }) => {
    if (key === 'add') {
      onConnect();
    } else if (key === 'disconnect') {
      confirmDisconnect();
    }
  };

  if (state === 'connected') {
    return (
      <div className={`${card} border-[#D3F8DF] md:items-start`}>
        <div className="flex items-start gap-3.5">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[10px] bg-[#ECFDF3] text-[#079455]">
            <CheckCircle size={22} weight="fill" />
          </span>
          <div>
            <div className="text-[16px] font-semibold text-[#101828]">
              ELD connected
            </div>
            {connectedTsps.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {connectedTsps.map((tsp) => (
                  <span
                    key={tsp}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-[#EAECF0] bg-[#F9FAFB] px-2.5 py-1 text-[12.5px] font-medium text-[#344054]"
                  >
                    <i className="h-1.5 w-1.5 rounded-full bg-[#0083bf]" />
                    {formatTspName(tsp)}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between gap-2.5 md:justify-end">
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-[#ABEFC6] bg-[#ECFDF3] px-2.5 py-1 text-[12px] font-semibold text-[#067647]">
            <CheckCircle size={13} weight="fill" />
            Connected
          </span>
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            disabled={disabled || loading}
            menu={{
              items: connectedMenuItems,
              onClick: handleConnectedMenuClick,
            }}
          >
            <button
              aria-label="Connection actions"
              disabled={disabled || loading}
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[8px] border border-[#EAECF0] bg-white text-[#667085] shadow-sm hover:bg-gray-50 hover:text-[#344054] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <DotsThree size={18} weight="bold" />
            </button>
          </Dropdown>
        </div>
      </div>
    );
  }

  if (state === 'needs_reconnect') {
    return (
      <div className={`${card} border-[#FECDCA] md:items-center`}>
        <div className="flex items-start gap-3.5">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[10px] bg-[#FEF3F2] text-[#D92D20]">
            <WarningCircle size={22} weight="fill" />
          </span>
          <div>
            <div className="text-[16px] font-semibold text-[#101828]">
              Reconnection needed
            </div>
            <div className="mt-0.5 max-w-[400px] text-[13px] leading-snug text-[#475467]">
              Your provider stopped sharing data. Reconnect to resume syncing.
            </div>
          </div>
        </div>
        <div className="flex flex-col items-stretch gap-2.5 md:items-end">
          <button
            disabled={disabled || loading}
            onClick={onConnect}
            className="w-full whitespace-nowrap rounded-[10px] border border-[#FDA29B] bg-white px-4 py-2.5 text-[14px] font-semibold text-[#B42318] hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 md:w-auto"
          >
            Reconnect
          </button>
          <button
            disabled={disabled || loading}
            onClick={confirmDisconnect}
            className="w-full whitespace-nowrap rounded-[9px] px-3.5 py-2 text-[13px] font-semibold text-[#667085] hover:text-[#344054] disabled:cursor-not-allowed disabled:opacity-60 md:w-auto"
          >
            Disconnect
          </button>
        </div>
      </div>
    );
  }

  // Not connected, and "started but never finished" (invited/viewed/accepted). These are the same
  // card: an unfinished link is not an error state, so it gets no warning styling — the only
  // difference is that we can drop the customer back where they left off instead of starting over.
  const unfinished = state === 'connecting';

  // Resuming and starting over are the same call now. The backend hands back the stored link when
  // the invitation is still live and mints a fresh one otherwise (arch #1550), so the UI no longer
  // has to guess whether a stored link is past Catena's 24h TTL — a guess that went stale in an
  // open tab and sent the customer to a dead page.

  return (
    <div className={`${card} md:items-start`}>
      <div className="flex items-start gap-3.5">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[10px] bg-[#EAF6FB] text-[#0083bf]">
          <PlugsConnected size={22} />
        </span>
        <div>
          <div className="text-[16px] font-semibold text-[#101828]">
            Connect your ELD
          </div>
          <div className="mt-0.5 max-w-[440px] text-[13px] leading-snug text-[#475467]">
            Securely link any of 200+ telematics providers — Samsara, Motive,
            Geotab, and more — for tailored financial insights.
            <WhatIsCatena />
          </div>
        </div>
      </div>
      <div className="flex flex-col items-stretch gap-1.5 md:items-end">
        <button
          disabled={disabled || loading}
          onClick={onConnect}
          className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-[10px] bg-[#101828] px-4 py-2.5 text-[14px] font-semibold text-white hover:bg-[#1D213B] disabled:cursor-not-allowed disabled:opacity-60 md:w-auto"
        >
          {loading ? <CircleNotch size={16} className="animate-spin" /> : null}
          {unfinished ? 'Finish connecting' : 'Connect ELD'}
        </button>
        {/* Recovery affordance for the case auto-reconcile deliberately skips: no local row at all
            (or only terminal ones) while Catena still holds a live link. An unfinished row is
            already reconciled on mount and on every poll tick, so it doesn't need this. */}
        {!unfinished && (
          <button
            disabled={disabled || loading}
            onClick={onSync}
            className="w-full whitespace-nowrap px-1 py-1.5 text-center text-[12px] font-medium text-[#667085] hover:text-[#0083bf] disabled:cursor-not-allowed disabled:opacity-60 md:w-auto md:py-0.5"
          >
            Already linked? Sync with Catena
          </button>
        )}
      </div>
    </div>
  );
}

export default CatenaIntegrationCard;
